import {
  Button,
  ButtonGroup,
  CircularProgress,
  ClickAwayListener,
  DEPRECATED_REACT_PROPS,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Tooltip,
  Typography,
} from '@chronosphereio/chrono-ui';
import { formatKeyboardShortcut, KeyboardShortcut } from '@chronosphereio/shared';
import { MenuDown as MenuDownIcon, MenuUp as MenuUpIcon, Refresh as RefreshIcon } from 'mdi-material-ui';
import * as React from 'react';
import { REFRESH_INTERVAL_DEFAULT_OPTIONS } from '@/components/RefreshIntervalPicker';
import { useResponsiveTimeRangeControls } from '@/components/utils/responsiveTimeRangeUtils';
import { useCancelInProgressDataQueries } from '@/plugin-system/runtime/data-querying';
import { RefreshInterval, useTimeRange } from '@/plugin-system/runtime/TimeRangeProviderV2';

const options: RefreshInterval[] = REFRESH_INTERVAL_DEFAULT_OPTIONS;

export type RunQueryButtonProps = {
  /**
   * Callback to run when the button is clicked
   */
  onClick: () => void;
  /**
   * Keyboard shortcut that will be displayed to the user.
   */
  keyboardShortcut?: KeyboardShortcut;
};

const RUN_QUERY_BUTTON_LABEL = 'Run';

export function RunQueryButton({ onClick, keyboardShortcut }: RunQueryButtonProps) {
  const { isFetching, cancelInProgressDataQueries } = useCancelInProgressDataQueries();

  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const { setRefreshInterval, refreshInterval } = useTimeRange();

  const handleClick = async () => {
    if (isFetching) {
      await cancelInProgressDataQueries();
    } else {
      onClick();
    }
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleRefreshIntervalChange = (value: RefreshInterval | undefined) => {
    setRefreshInterval(value);
    handleClose();
  };

  const handleClose = (event?: Event) => {
    if (event && anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const { responsiveVariant } = useResponsiveTimeRangeControls();
  const isCompactVariant = responsiveVariant === 'compact';

  const hasKeyboardShortcut = keyboardShortcut !== undefined;
  const keyboardShortcutTooltip = hasKeyboardShortcut ? (
    <Stack direction="row" spacing={1} alignItems="center">
      <Typography variant="body2">Run query</Typography>
      <Typography variant="body2">({formatKeyboardShortcut(keyboardShortcut)})</Typography>
    </Stack>
  ) : (
    ''
  );
  const getWidthOfAnchorElement = () => {
    if (anchorRef.current && refreshInterval) {
      return anchorRef.current.offsetWidth;
    }
    return undefined;
  };

  return (
    <React.Fragment>
      <ButtonGroup
        variant="contained"
        aria-label={RUN_QUERY_BUTTON_LABEL}
        sx={{
          '.MuiButtonGroup-grouped:not(:last-of-type)': {
            borderColor: (t) => t.palette.primary.contrastText,
          },
        }}
      >
        <Tooltip Content={isFetching ? 'Cancel queries' : keyboardShortcutTooltip}>
          <Button
            StartIcon={isFetching ? undefined : <RefreshIcon />}
            onClick={handleClick}
            sx={{
              whiteSpace: 'nowrap',
              // Smaller width for small variant where we just show the refresh icon..
              width: isCompactVariant ? 32 : 74,
              // !important is needed to override the default min with of buttons inside a button group.
              minWidth: isCompactVariant ? '32px !important' : undefined,
              ['.MuiButton-startIcon']: {
                // Center the icon in the small variant.
                margin: isCompactVariant ? 'auto' : undefined,
              },
            }}
          >
            {isFetching ? (
              <CircularProgress size={12} sx={{ color: (t) => t.palette.text.inversePrimary }} />
            ) : (
              !isCompactVariant && RUN_QUERY_BUTTON_LABEL
            )}
          </Button>
        </Tooltip>
        {!isCompactVariant && (
          <Button
            sx={{
              '.MuiButton-endIcon': {
                marginLeft: refreshInterval ? undefined : 0,
                marginRight: refreshInterval ? undefined : 0,
              },
              paddingInline: refreshInterval ? undefined : 0,
            }}
            ref={anchorRef}
            EndIcon={open ? <MenuUpIcon /> : <MenuDownIcon />}
            aria-controls={open ? 'refresh-interval' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-label="Select refresh interval"
            aria-haspopup="menu"
            onClick={handleToggle}
          >
            {refreshInterval}
          </Button>
        )}
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 1,
        }}
        open={open}
        placement="bottom-start"
        anchorEl={anchorRef.current}
        transition
        role="menu"
        disablePortal
        {...DEPRECATED_REACT_PROPS}
      >
        {({ TransitionProps }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: 'center top',
            }}
          >
            <Paper sx={{ width: getWidthOfAnchorElement() }}>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList autoFocusItem>
                  <MenuItem onClick={() => handleRefreshIntervalChange(undefined)}>Off</MenuItem>
                  {options.map((option) => (
                    <MenuItem key={option} onClick={() => handleRefreshIntervalChange(option)}>
                      {option}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
}
